// Offer attachment uploader — disabled until a draft offer exists
// (pumppuOfferId required: the metadata POST is keyed by it). Reuses the
// wizard's SAS upload pipeline via uploadPumppuOfferAttachment.

import { useId, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import { useAuth } from "src/context/AuthContext";
import { useCurrentPerson } from "src/utils/useCurrentPerson";
import { useT } from "src/i18n";
import { uploadPumppuOfferAttachment, deleteAttachment } from "src/utils/quote/quoteAttachments";
import { captureError } from "src/utils/errorReporting";

export default function OfferAttachments({ pumppuOfferId, attachments = [], onChange = () => {} }) {
    const { token } = useAuth();
    const person = useCurrentPerson();
    const t = useT();
    const inputId = useId();
    const [busy, setBusy] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const disabled = !pumppuOfferId || !person;

    const upload = async (files) => {
        if (disabled || files.length === 0) return;
        setBusy(true);
        setUploadError(null);
        const next = [...attachments];
        const failed = [];
        for (const file of files) {
            try {
                const result = await uploadPumppuOfferAttachment({
                    file,
                    pumppuOfferId,
                    token,
                    ownerAsiakasId: person.ownerAsiakasId,
                    entryByPersonId: person.personId,
                });
                if (result?.attachmentId != null) {
                    next.push({ ...result });
                }
            } catch (err) {
                // Per-file isolation: one bad file must not hide the batch's successes.
                captureError(err, { tags: { entity: "attachment", operation: "offer_upload" } });
                failed.push(file.name);
            }
        }
        onChange(next);
        if (failed.length > 0) {
            setUploadError(t("provider.offerAttachments.uploadFailed", { files: failed.join(", ") }));
        }
        setBusy(false);
    };

    const remove = async (attachmentId) => {
        try {
            await deleteAttachment({ attachmentId, token });
            onChange(attachments.filter((a) => a.attachmentId !== attachmentId));
        } catch (err) {
            captureError(err, { tags: { entity: "attachment", operation: "offer_delete" } });
        }
    };

    return (
        <Stack spacing={1}>
            <Box
                sx={{
                    border: 1, borderStyle: "dashed", borderColor: "divider",
                    borderRadius: 1, p: 2, textAlign: "center",
                    opacity: disabled ? 0.5 : 1,
                    pointerEvents: disabled ? "none" : "auto",
                }}
            >
                <input
                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                    style={{ display: "none" }}
                    id={inputId}
                    type="file"
                    multiple
                    onChange={(e) => {
                        upload(Array.from(e.target.files));
                        e.target.value = null;
                    }}
                />
                <label htmlFor={inputId}>
                    <Button variant="outlined" component="span" disabled={disabled || busy} size="small">
                        {busy ? t("common.loading") : t("provider.offerAttachments.selectFilesCta")}
                    </Button>
                </label>
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 1 }}>
                    {disabled
                        ? t("provider.offerAttachments.disabledHint")
                        : t("provider.offerAttachments.acceptedTypesHint")}
                </Typography>
            </Box>

            {uploadError && (
                <Typography variant="caption" color="error">{uploadError}</Typography>
            )}

            {attachments.map((a) => (
                <Stack key={a.attachmentId} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {a.origFileName}
                    </Typography>
                    <IconButton size="small" onClick={() => remove(a.attachmentId)} aria-label={t("provider.offerAttachments.removeAria", { filename: a.origFileName })}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ))}
        </Stack>
    );
}
